import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm, FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { Setting } from '../../common/settings';
import { TaskDetail, TaskUpdate } from '../model/taskDetail';
import { TaskService } from '../services/tasks.service';
import url from 'src/settings.json';
import { Task } from '../../requests/Model/task';
import { HttpClient, HttpParams } from '@angular/common/http';
import { OrganisationService } from '../../organisation/organisation/services/organisation.service';
import { BugDetail } from '../model/bugDetail';
import { AuthService } from '../../auth/service/auth.service';

@Component({
  selector: 'app-task',
  templateUrl: './task.component.html',
  styleUrls: ['./task.component.css']
})
export class TaskComponent implements OnInit, OnDestroy {
  bugControl = new FormControl();  
  tasksubscribtion?: Subscription;
  @Input() taskId?: number;
  taskDetail!: any;
  @ViewChild('taskSubmitForm', { static: false })  
  taskSubmitForm!: NgForm;
  taskSave: TaskUpdate = { id: 0, status: "",justification:"" };
  isTaskloading = false;
  isTasksubmitting = false;
  taskColor = '';  
  isBugLoading = false;
  bugList: Array<Task> = [];
  bugOptions: Array<Task> = [];
  bugsfilteredOptions!: Observable<any>;
  relatedBugs: Array<BugDetail> = [];
  setting: Setting = url;
  currentOrganisation: any;
  organisationSubscriber?: Subscription;
  taskSaveSubscribtion?: Subscription;
  taskSubmitted = false;
  errorMessage!: string;
  isError = false;
  dataid = '';
  @Input() id = 0;

  constructor(private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly oAuthService: AuthService,
    private readonly organisationService: OrganisationService,
    private readonly taskService: TaskService) {
      this.organisationSubscriber = this.organisationService.organisationEmitter.subscribe({
        next: (value: string) => {
          if (value === '') { this.currentOrganisation = this.organisationService.getLocalStorageItem("CurrentOrganisation") }
          else { this.currentOrganisation = value; }
        }
      });
      this.taskId = this.id;   
  }

  ngOnDestroy(): void {
    this.tasksubscribtion?.unsubscribe();
    this.taskSaveSubscribtion?.unsubscribe();
  }

  ngOnInit(): void {        
    this.taskId = this.id; 
    this.dataid = String(this.id);
    this.getTaskDetails(Number(this.taskId));
    this.bugsfilteredOptions = this.bugControl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      filter((value)=>value !== ""),
      switchMap(val => {
        return this.filterBugs(val)
      }))
  }

  filterBugs(val: string): Observable<Task[]> {
    // call the service which makes the http-request    
    return this.getBugs(val)
      .pipe(
        map(response => response?.filter((option: Task) => {
          this.isBugLoading = false;
          return option.title;
        })))

  }
  
  getBugs(val: string) {
    this.isBugLoading = true;
    const url = `${this.setting.url}/${this.currentOrganisation}`;
    const proj = this.taskDetail.project;
    const token = this.oAuthService.getAccessToken();
    const headers = { 'Authorization': `Bearer ${token}` }
    const term = new HttpParams().set('term', val).set('project', proj).set('requirementId', this.taskDetail.requirmentId);
    return this.bugOptions?.length ?
      of(this.bugOptions) :
      this.http.get<Task[]>(`${url}/api/bugs/`, { headers: headers, params: term }).pipe(tap(data => this.bugOptions = data))
  } 

  getTaskDetails(id: number) {
    this.isTaskloading = true;
    this.tasksubscribtion = this.taskService.getTaskDetails(id).subscribe(
      {
        next: (response: TaskDetail) => {
          response.approvalStatus=response.approvalStatus.replace(/\s/g, "")
          this.taskDetail = response;          
          this.relatedBugs = this.taskDetail.bugs;
        },
        error: (error) => { console.log(error) ;
          this.isError = true;
          this.errorMessage = error.error;

          switch (error.status) {
            case 400:
              this.errorMessage = "The task ID is not valid!";
              error.statusText = 'BadRequest'
              break;
            case 401:
              this.errorMessage = "You are not authroized to view the task!";
              error.statusText = 'Unauthorized'
              break;              
            case 404:              
              this.errorMessage = 'The task with id ${id} is not found!';                            
              error.statusText = 'NotFound'
              break;
            default:
              this.errorMessage = "The task not found!";
              error.statusText = 'BadRequest'
              break;
          }         
                    
          console.log('Error in loading Task details for ${id}! Error Message: ${this.errorMessage}');
          this.isTaskloading = false;
        },
        complete: () => {
          this.getCSSColorStatus();
          this.isTaskloading = false;
        }
      }      
    );    
  }

  onBugSelected(event: MatAutocompleteSelectedEvent) {
    var bug = this.bugOptions.find(x => x.id === event.option.value) as Task;
    console.log(bug);
    this.bugList.push(new Task(bug.shortType, event.option.value, bug.title));
    this.bugOptions = [];
    this.bugControl.setValue("");
    event.option.value = '';
  }

  removeBug(bug: Task) {
    const index = this.bugList.indexOf(bug);
    this.bugList.splice(index, 1);
  }

  onTaskSubmit() {
    const response = this.taskSubmitForm.value.reviewResponseType;
    this.taskSave.id = this.taskId;
    this.taskSave.status = response;
    if (this.taskDetail.justificationType === 'BugId' && response === "Rejected") {      
      this.taskSave.justification = this.bugList.map(b => b.id).join(',');
    }
    else if (this.taskDetail.justificationType === 'FreeText' && response === "Rejected") {
      this.taskSave.justification = this.taskSubmitForm.value.justification;
    }
    else if (response === "CouldNotTest") {
      this.taskSave.justification = this.taskSubmitForm.value.justification;
    }
    this.isTasksubmitting = true;
    this.taskSubmitted = true;
    this.taskSaveSubscribtion=this.taskService.saveResponse(this.taskSave).subscribe({
      next: () => {
        this.taskSubmitted = false;
        this.isTasksubmitting = false;
        this.taskDetail.approvalStatus = this.taskSave.status;
        this.router.navigate(['/tasks']);
      },
      error: (err) => { this.isError = true; this.errorMessage = err.error; this.isTaskloading = false; this.taskSubmitted = false; },
      complete: () => {
        this.isTasksubmitting = false;
        this.taskSubmitted = false;
      }
    });
    
  }
  onrequestnavigate(requestId: number) {
    const url = this.router.createUrlTree([`/request/detail/${requestId}`], { relativeTo: this.route }).toString();
    window.open(url, '_blank');
  }
  getCSSColorStatus(): string {
    return this.taskService.getCSSColorforTask(this.taskDetail.approvalStatus);     
  }
}
